import * as Dialog from '@radix-ui/react-dialog'
import type { ReactNode } from 'react'

import { FxIcon } from '@/components/fx'

/**
 * The dialog shell this tab's four confirmations sit in: `radius-xl`,
 * `shadow-overlay`, no border, on the scrim the design draws behind every
 * dialog.
 *
 * It is a near-copy of `pages/projekte/ProjektDialog` on purpose. There is no
 * `FxDialog` in `components/fx` — only `FxUploadDialog`, which is one
 * specific dialog rather than a shell — and `components/ui/dialog` is the
 * shadcn one, bordered and `rounded-lg`, which looks like a different product
 * beside an Fx panel. Importing another screen's private component would tie
 * two unrelated pages together for the sake of 40 lines. **This belongs in
 * `components/fx` as `FxDialog`** the moment somebody owns that file;
 * reported.
 *
 * Radix supplies the focus trap, Escape, the scroll lock and the
 * `aria-labelledby` wiring.
 */
export function CockpitDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/55" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-[min(560px,calc(100vw-40px))] -translate-x-1/2 -translate-y-1/2 flex-col gap-5 overflow-y-auto rounded-xl bg-surface p-8 text-body text-text shadow-overlay">
          <div className="flex items-start gap-4">
            <Dialog.Title className="m-0 flex-1 text-dialog-title">{title}</Dialog.Title>
            <Dialog.Close
              className="-mr-2 -mt-2 flex h-11 w-11 flex-none items-center justify-center rounded-sm text-text"
              aria-label="Dialog schließen"
            >
              <FxIcon name="close" size={24} />
            </Dialog.Close>
          </div>
          {description && (
            <Dialog.Description className="m-0 text-body text-text-muted">
              {description}
            </Dialog.Description>
          )}
          {children}
          {footer && <div className="flex flex-wrap justify-end gap-4">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
